import React from "react";

import { type DropdownMenuItem } from "../dropdown-menu/DropdownMenu";
import ContractMessageRow from "./ContractMessageRow";
import { buildContractActionMenuItems } from "./contractActionMenu";
import ContractCard, {
  type ContractCardOfferSummary,
  type ContractCardSide,
} from "./ContractCard";
import type { SubcontractPdfPreviewState } from "../offer-wizard/SubcontractContractPdfPreview";
import type { ContractCardPhase } from "../../utils/offerStatus";
import type { DmMessage } from "../../app/services/dmMessages";
import type { DmPendingAmendment } from "../../app/services/dmThreads";

interface ContractTimelineItemProps {
  message: DmMessage;
  isOurs: boolean;
  showAvatar: boolean;
  /** 既読フィルタ用の現在ユーザー ID（通常メッセージと同じ既読表示のため）。 */
  currentUserId?: number | string;
  phase: ContractCardPhase;
  /** 有効期限。`phase === "pre"` のときカウントダウン対象になる。 */
  expiresAt?: string | null;
  /** 締結日時。`phase` が post / inspected のとき有効期限の代わりに表示する。 */
  concludedAt?: string | null;
  /** オファー送信時の初期メッセージ本文（カード上部に表示）。 */
  initialMessage?: string | null;
  /** オファー詳細サマリ（カード中央に表示）。 */
  offerSummary?: ContractCardOfferSummary | null;
  onConfirm?: () => void;
  onEdit?: () => void;
  onReject?: () => void;
  onInvalidate?: () => void;
  onCloseRequest?: () => void;
  /** 契約書を更新（3点メニュー）。締結後・送信者のみ。 */
  onContractUpdate?: () => void;
  /**
   * 承認待ちの契約更新提案。提案中は「契約書を更新」を無効化するために使う。
   * 差分と承認 / 取り下げの導線は、提案された時点の位置に別アイテムとして出す
   * （`DM.tsx` の `contract_amendment_proposed` 分岐）。
   */
  pendingAmendment?: DmPendingAmendment | null;
  /** 締結後に表示する契約書 PDF プレビュー状態（動的生成）。 */
  contractPdfPreview?: SubcontractPdfPreviewState | null;
  /** この版がまだ有効か（契約更新後は `false` になり「無効」表示になる）。 */
  isContractActive?: boolean;
  /**
   * このスレッドの contractor が同一プロジェクトで進行中の再委託オファー
   * （子オファー）を抱えているか。`true` のときは親側で escrow を巻き戻す
   * 操作（「クローズを相談」など）を抑止する。完全な cascade-guard が
   * 実装されるまでの暫定 UI ガード。
   */
  hasActiveChildSubcontracts?: boolean;
}

/**
 * タイムライン上の契約書アイテム。
 *
 * `system_content.kind === "contract_uploaded"` のメッセージに対して、
 * 通常メッセージと同じ左右非対称レイアウト（`ContractMessageRow`）の中に
 * `ContractCard` を描画する。状態（締結前/締結後/検収後/無効）は
 * `thread.status_timestamps` から導出した `phase` に応じて見た目とアクション
 * ボタンを切り替える。
 *
 * メタ列（縦中央の 3 点メニュー / 作成日・既読日時 / 既読アイコン）は通常メッセージ
 * と同じ `MessageMetaColumn` を用いて表示を統一する。締結後（post）かつ送信者の場合、
 * 3 点メニューに「クローズを相談 / 契約書を更新」を出す。
 */
const ContractTimelineItem: React.FC<ContractTimelineItemProps> = ({
  message,
  isOurs,
  showAvatar,
  currentUserId,
  phase,
  expiresAt,
  concludedAt,
  initialMessage,
  offerSummary,
  onConfirm,
  onEdit,
  onReject,
  onInvalidate,
  onCloseRequest,
  onContractUpdate,
  pendingAmendment,
  contractPdfPreview,
  isContractActive = true,
  hasActiveChildSubcontracts = false,
}) => {
  const side: ContractCardSide = isOurs ? "sender" : "receiver";

  // 締結後（post）かつ送信者のみ 3 点メニューを表示する。
  // 「クローズを相談」は ContractCard のボタンから本メニューへ移設した。
  //
  // 契約が更新されるとタイムラインに複数の版が並ぶので、操作は **現在有効な版**
  // にだけ出す。更新後はこのアイテムが「無効」になり、メニューと承認待ちの導線は
  // 新しい契約アイテム（`ContractAmendedTimelineItem`）側へ移る。
  const showActions = phase === "post" && isOurs && isContractActive;
  const menuItems: DropdownMenuItem[] = showActions
    ? buildContractActionMenuItems({
        onCloseRequest,
        onContractUpdate,
        hasActiveChildSubcontracts,
        pendingAmendment,
      })
    : [];

  return (
    <ContractMessageRow
      message={message}
      isOurs={isOurs}
      showAvatar={showAvatar}
      currentUserId={currentUserId}
      menuItems={menuItems}
    >
      <ContractCard
        phase={phase}
        side={side}
        expiresAt={expiresAt}
        concludedAt={concludedAt}
        initialMessage={initialMessage}
        offerSummary={offerSummary}
        contractPdfPreview={contractPdfPreview}
        isContractActive={isContractActive}
        onConfirm={onConfirm}
        onEdit={onEdit}
        onReject={onReject}
        onInvalidate={onInvalidate}
      />
    </ContractMessageRow>
  );
};

export default ContractTimelineItem;
